<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';

import { LanguageType } from '@/__generated__/gateway/graphql';
import WishlistButton from '@/components/MyHobbii/views/MyWishlist/WishlistButton.vue';
import AddPatternToStashModal from '@/shared/components/patterns/modals/AddPatternToStashModal.vue';
import PatternLanguageModal from '@/shared/components/patterns/modals/PatternLanguageModal.vue';
import PatternDownloadRetry from '@/shared/components/patterns/PatternDownloadRetry.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import IconDownload from '@/shared/components/UI/Icons/IconDownload.vue';
import Image from '@/shared/components/UI/Image.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import SkeletonLoader from '@/shared/components/UI/SkeletonLoader.vue';
import UserDetailsModal from '@/shared/components/user/UserDetailsModal.vue';
import { useContactUuid } from '@/shared/composables/useContactUuid';
import { useTranslations } from '@/shared/composables/useTranslations';
import { getStashPatternsUrl } from '@/shared/constants/accountUrls';
import {
  campaignType,
  posthogEvents,
  userCookies,
  userDetailsFormSources
} from '@/shared/constants/constants';
import { patternDownloadGiftCardAmounts } from '@/shared/constants/shopify';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { useProductStore } from '@/shared/pinia/modules/product';
import { type CraftSelection, useUserStore } from '@/shared/pinia/modules/user';
import { useWishlistStore } from '@/shared/pinia/modules/wishlist';
import {
  marketingSignupSources,
  submitMarketingSignup,
  toWireCraftSkillLevels
} from '@/shared/services/marketingSignupAPI';
import { StashApiService } from '@/shared/services/stashAPI';
import {
  trackUserDetailsFormFieldFilled,
  trackUserDetailsFormShown,
  trackUserDetailsFormSubmitted,
  type UserDetailsFormField
} from '@/shared/tracking/user-details-form';
import { getCookie, setCookie } from '@/shared/utils/cookie';
import { getMarketCountryCode } from '@/shared/utils/country';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

const IS_DEV = import.meta.env.DEV;
import { getLanguageType } from '@/shared/utils/gateway/helpers';
import { setCartButtonBlockVisible } from '@/shared/utils/product';
import { formattedPrice } from '@/shared/utils/shopify/money-formatter';
import {
  type DownloadPatternData,
  hashUserData,
  klaviyoIdentify,
  trackDownloadPattern,
  trackHobbiiEvent
} from '@/shared/utils/tracking';
import { hobbiiSessionStorage } from '@/shared/utils/web-storage';

const { t } = useTranslations();

const SIGNUP_ENRICHMENT_STORAGE_KEY = 'signupEnrichment';

// 400 days, the longest lifetime Chrome honours on a cookie; anything larger is
// silently clamped to it. Must match what the campaign app block writes, or one
// browser ends up with two identities depending on which form it used.
const IDENTITY_COOKIE_DAYS = 400;

type ModalSubmittedDetails = {
  firstName: string;
  email: string;
  crafts: CraftSelection[];
  website: string;
  formFilledInMs: number;
};

type PendingEnrichment = ModalSubmittedDetails & {
  // Captured at submission, before the native form's redirect — reading
  // document.referrer after the redirect would return Shopify's own form
  // handler URL, not the page that actually sent the visitor here.
  referrer: string;
  // Minted here for the same reason: it belongs to the submission, not to the
  // request that happens to carry it. Stashing it means a resume that ran twice
  // could not produce two ids for one signup.
  eventId: string;
};

const props = defineProps<{
  productName: string;
  productSku: string;
  patternPurchasable: boolean;
  productImageUrl: string;
  localeLanguage?: string;
  localeCountry?: string;
  localeMarket?: string;
}>();

const configStore = useConfigStore();
const productStore = useProductStore();
const userStore = useUserStore();
const wishlistStore = useWishlistStore();

// --- State for modals and download logic ---
const showAddToStashModal = ref(false);
const showPatternLanguageModal = ref(false);
const showPatternDownloadModal = ref(false);

// --- Tracks if the authenticated user has previously purchased the paid pattern ---
const hasPreviousPurchase = ref(false);

// --- Determines if the user can immediately download the pattern ---
// For paid patterns: true if authenticated and has previous purchase
// For free patterns: true if they've already given their details, or
// explicitly skipped the form via "already signed up"
const canDownloadImmediately = computed(() => {
  if (props.patternPurchasable) {
    return userStore.isLogged && hasPreviousPurchase.value;
  } else {
    return userStore.capturedProfile !== null || isSignedUp();
  }
});

const emarsysUUID = useContactUuid();
const isCheckingOwnership = ref(props.patternPurchasable && userStore.isLogged);

// Owners download instead of buying, so the cart row (which also holds the
// wishlist) stays hidden and the wishlist sits next to Download.
async function resolveOwnership(): Promise<void> {
  const url = await getPatternDownloadUrl();
  if (url) {
    hasPreviousPurchase.value = true;
    productStore.purchasable = false;
  } else {
    setCartButtonBlockVisible(true);
  }
  isCheckingOwnership.value = false;
}

// --- On mount, update newsletter signup state and check for previous purchase (paid patterns) ---
onBeforeMount(async () => {
  const ownershipCheck = isCheckingOwnership.value
    ? resolveOwnership()
    : Promise.resolve();

  // Load wishlist store to ensure wishlist state is available for the button
  // This will load cached items immediately and fetch fresh data in background
  if (userStore.isLogged) {
    try {
      await wishlistStore.loadWishlistItems();
    } catch (error) {
      notifyBugsnagWithContext('PatternDownload', error, {
        message: 'Failed to load wishlist',
        data: {
          productSku: props.productSku,
          productName: props.productName
        }
      });
      if (IS_DEV) {
        console.error('[PatternDownload] Failed to load wishlist:', error);
      }
    }
  }

  await updateNewsletterSignupState();
  await ownershipCheck;
  // After details capture: show language selection modal, and fire the
  // enrichment submission now that we're back from the native form's
  // redirect (it can't fire before — the redirect is a full page
  // navigation, so nothing in-memory survives it).
  if (hobbiiSessionStorage.getItem('signupForm') === 'user-details-form') {
    showPatternLanguageModal.value = true;
    hobbiiSessionStorage.removeItem('signupForm');

    const pendingEnrichmentRaw = hobbiiSessionStorage.getItem(
      SIGNUP_ENRICHMENT_STORAGE_KEY
    );
    if (pendingEnrichmentRaw) {
      hobbiiSessionStorage.removeItem(SIGNUP_ENRICHMENT_STORAGE_KEY);
      await submitEnrichment(JSON.parse(pendingEnrichmentRaw));
    }
  }
});

async function getPatternDownloadUrl(): Promise<string | null> {
  return await productStore.patternDownloadUrl(
    props.productSku,
    emarsysUUID.value || '',
    getCookie('localization') || ''
  );
}

async function updateNewsletterSignupState(): Promise<void> {
  if (userStore.isLogged) {
    setAsSignedUp();
  } else if (!emarsysUUID.value) {
    // We need to prompt them for their email again so we can track the pattern
    // download. (They can always skip it by clicking "already signed up")
    setAsNotSignedUp();
  }
}

watch(showPatternDownloadModal, (newVal) => {
  trackHobbiiEvent(newVal ? 'show popup' : 'close popup', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });

  if (newVal) {
    trackUserDetailsFormShown(userDetailsFormSources.PATTERN_DOWNLOAD, {
      pattern_name: props.productName,
      pattern_sku: props.productSku
    });
  }
});

watch(showAddToStashModal, (active) => {
  if (active) saveToStashError.value = null;
});

const onUserDetailsFieldFilled = (field: UserDetailsFormField) => {
  trackUserDetailsFormFieldFilled(
    field,
    userDetailsFormSources.PATTERN_DOWNLOAD,
    {
      pattern_name: props.productName,
      pattern_sku: props.productSku
    }
  );
};

const patternDownloadAnonymous = () => {
  trackHobbiiEvent('clicked skip form cta', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });

  window.posthog?.capture(posthogEvents.PATTERN_DOWNLOAD_ANONYMOUS, {
    'Content Locking': campaignType.HOBBII,
    'Pattern Name': props.productName
  });

  setAsSignedUp(); // To avoid annoying users by asking for email every time
  showPatternDownloadModal.value = false;
  showPatternLanguageModal.value = true;
};

// --- Error state for download failures ---
const downloadErrorText = t('patterns.download.error_fetching_url');
const showDownloadError = ref(false);
const downloadErrorMessage = computed(() =>
  showDownloadError.value ? downloadErrorText : null
);
// Kept apart from showDownloadError, which disables the main Download button:
// a failed retry must leave that button usable once the modal is closed.
const showRetryError = ref(false);
const retryErrorMessage = computed(() =>
  showRetryError.value ? downloadErrorText : null
);
// --- Loading state for download action ---
const isLoading = ref(false);
const showDownloadSuccessModal = ref(false);

function getDownloadPatternTrackingParams(): DownloadPatternData {
  const variantIdNum = parseInt(productStore.variantId ?? '', 10);
  const productIdNum = parseInt(productStore.productId ?? '', 10);
  return {
    category: props.patternPurchasable ? 'plus_pattern' : 'free_pattern',
    action: 'download_pattern',
    label: props.productName,
    item_id: productStore.legacyId || '', // This was for backwards compatibility when migrating away from OC to keep events in sync, can be removed a year after completed migration as it wont be relevant.
    sku: props.productSku,
    market: configStore.countryCode || getMarketCountryCode(),
    currency: window.Shopify.currency.active,
    variant_id: Number.isFinite(variantIdNum) ? variantIdNum : null,
    product_id: Number.isFinite(productIdNum) ? productIdNum : null
  };
}

// Each download fetches a fresh signed URL: once one expires, the error
// response is not an attachment and would navigate the shopper off the shop.
const downloadPaidPattern = async (): Promise<boolean> => {
  isLoading.value = true;
  const url = await getPatternDownloadUrl();
  isLoading.value = false;
  if (!url) return false;
  location.href = url;
  return true;
};

const startDownload = async () => {
  showDownloadError.value = false;
  if (!(await downloadPaidPattern())) {
    showDownloadError.value = true;
    return;
  }
  closeDownloadModal();
  showRetryError.value = false;
  showDownloadSuccessModal.value = true;

  trackDownloadPattern(getDownloadPatternTrackingParams());
};

const retryPaidDownload = async () => {
  showRetryError.value = false;
  showRetryError.value = !(await downloadPaidPattern());
};

// --- Handles download button click for free patterns (newsletter-gated)
const handleDownloadButtonClick = () => {
  if (!props.patternPurchasable && userStore.isLogged) {
    showAddToStashModal.value = true;
    return;
  }
  if (canDownloadImmediately.value) {
    if (props.patternPurchasable) {
      startDownload();
    } else {
      showPatternLanguageModal.value = true;
    }
  } else {
    openDownloadModal();
  }
};

const openDownloadModal = () => {
  showPatternDownloadModal.value = true;
};

const closeDownloadModal = () => {
  showPatternDownloadModal.value = false;
};

const onJustPdf = () => {
  showAddToStashModal.value = false;
  showPatternLanguageModal.value = true;
};

const onPatternLanguageDownloaded = () => {
  trackDownloadPattern(getDownloadPatternTrackingParams());
};

const saveToStashLoading = ref(false);
const saveToStashError = ref<string | null>(null);

const onSaveToStash = async () => {
  saveToStashError.value = null;
  saveToStashLoading.value = true;
  try {
    const locale = getLanguageType(
      window.Shopify?.locale ?? 'en',
      LanguageType.En
    );
    const success = await StashApiService.savePattern(props.productSku, locale);
    if (success) {
      showAddToStashModal.value = false;
      window.location.assign(getStashPatternsUrl());
    } else {
      saveToStashError.value = t('patterns.add_to_stash_modal.save_error');
    }
  } catch (error) {
    saveToStashError.value = t('patterns.add_to_stash_modal.save_error');
    notifyBugsnagWithContext(
      'PatternDownload.onSaveToStash',
      error instanceof Error ? error : new Error(String(error)),
      {
        message: 'Save to stash failed (thrown error)',
        data: { productSku: props.productSku }
      }
    );
  } finally {
    saveToStashLoading.value = false;
  }
};

const onUserDetailsSubmitted = (details: ModalSubmittedDetails) => {
  // Submitting the form is the newsletter signup — there's no separate
  // tick box (per design, not the earlier written spec).
  setAsSignedUp();

  // Fired here, at submission, rather than in submitEnrichment — that call
  // only resolves after the native form's redirect, and is gated on the
  // enrichment API succeeding. This event exists so a submission that never
  // makes it to a successful enrichment (lost tab, API failure) still shows
  // up as a funnel step, distinct from a signup that never got submitted.
  trackUserDetailsFormSubmitted(userDetailsFormSources.PATTERN_DOWNLOAD, {
    pattern_name: props.productName,
    pattern_sku: props.productSku
  });

  hobbiiSessionStorage.setItem('signupForm', 'user-details-form');
  // The native form submit below navigates away, so nothing in memory
  // survives — stash what the enrichment call needs (including the
  // referrer, which has to be read now rather than after the redirect)
  // and pick it back up in onBeforeMount once we're back.
  const pendingEnrichment: PendingEnrichment = {
    ...details,
    referrer: document.referrer,
    eventId: crypto.randomUUID()
  };
  hobbiiSessionStorage.setItem(
    SIGNUP_ENRICHMENT_STORAGE_KEY,
    JSON.stringify(pendingEnrichment)
  );

  klaviyoIdentify({
    email: details.email,
    firstName: details.firstName || undefined
  });
};

const submitEnrichment = async ({
  firstName,
  email,
  crafts,
  website,
  formFilledInMs,
  referrer,
  eventId
}: PendingEnrichment) => {
  try {
    const { hobbii_cognito_uuid: identityUuid } = await submitMarketingSignup({
      email,
      name: firstName || undefined,
      source: marketingSignupSources.PATTERN_DOWNLOAD,
      subscribed: true,
      craftSkillLevels: crafts.length
        ? toWireCraftSkillLevels(crafts)
        : undefined,
      origin: {
        language: props.localeLanguage || undefined,
        country: props.localeCountry || undefined,
        market: props.localeMarket || undefined,
        referrer: referrer || undefined
      },
      event_id: eventId,
      website,
      formFilledInMs
    });

    // The endpoint cannot set this itself — Shopify strips `Set-Cookie` from an
    // app proxy response — so we persist what it minted. We never mint one here:
    // an absent key means the submission resolved no identity, and an empty
    // cookie would be worse than none. It cannot be HttpOnly either, since a
    // cookie written from JavaScript never can.
    if (identityUuid) {
      setCookie(
        userCookies.HOBBII_COGNITO_UUID,
        identityUuid,
        IDENTITY_COOKIE_DAYS
      );
    }
  } catch (error) {
    notifyBugsnagWithContext('MarketingSignupError', error, {
      message: 'Failed to submit pattern-download signup',
      data: { source: marketingSignupSources.PATTERN_DOWNLOAD }
    });
    return;
  }

  trackHobbiiEvent('pattern_download_signup', {
    category: 'newsletter',
    customer_name_hash: firstName && hashUserData(firstName),
    customer_email_hash: email && hashUserData(email),
    currency: window.Shopify.currency.active
  });

  trackHobbiiEvent('clicked on signup', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });

  window.posthog?.capture(posthogEvents.NEWSLETTER_SIGNUP, {
    'Content Locking': campaignType.HOBBII,
    'Pattern Name': props.productName
  });
};

function setAsSignedUp() {
  setCookie(userCookies.NEWSLETTER_SIGNUP, '1', 400);
}
function setAsNotSignedUp() {
  setCookie(userCookies.NEWSLETTER_SIGNUP, '0', 400);
}

// --- Checks if the user has signed up for the newsletter (free patterns) ---
function isSignedUp() {
  return getCookie(userCookies.NEWSLETTER_SIGNUP) === '1';
}

// --- Computed for unified download button ---
const isOwnedPaidPattern = computed(
  () => props.patternPurchasable && canDownloadImmediately.value
);

const buttonClickHandler = computed(() =>
  isOwnedPaidPattern.value ? startDownload : handleDownloadButtonClick
);

const buttonLabel = computed(() =>
  isOwnedPaidPattern.value
    ? t('patterns.download.download_pattern')
    : t('patterns.download.download_free_pattern')
);

const productGroupIdentifier = computed(
  () => productStore.productGroupId ?? undefined
);

// Get SKU for wishlist - use productSku prop or try to extract from product store
const wishlistSku = computed<string | undefined>(() => {
  if (props.productSku) {
    return props.productSku;
  }

  return undefined;
});

const formattedGiftCardPrice = computed(() => {
  const locale = window.Shopify?.locale || 'en';
  const amountInCents =
    patternDownloadGiftCardAmounts[
      locale as keyof typeof patternDownloadGiftCardAmounts
    ] || patternDownloadGiftCardAmounts.en;
  return formattedPrice(amountInCents);
});

const PRODUCT_NAME_DISPLAY_LIMIT = 40;

const truncatedProductName = computed(() =>
  props.productName.length > PRODUCT_NAME_DISPLAY_LIMIT
    ? `${props.productName.slice(0, PRODUCT_NAME_DISPLAY_LIMIT)}...`
    : props.productName
);
</script>

<template>
  <!-- Error message above the buttons -->
  <div v-if="downloadErrorMessage" class="mb-2 font-bold text-error-600">
    {{ downloadErrorMessage }}
  </div>

  <div v-if="isCheckingOwnership" class="mt-6 flex gap-2">
    <SkeletonLoader type="image" class="h-14 flex-1 !rounded-lg" />
    <SkeletonLoader type="image" class="size-14 !rounded-lg" />
  </div>

  <div
    v-else-if="isOwnedPaidPattern || !patternPurchasable"
    :class="['flex items-start gap-2', { 'mt-6': patternPurchasable }]"
  >
    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <ButtonText
        class="h-14 whitespace-nowrap rounded-lg text-lg sm:!px-6 md:text-base"
        :loading="isLoading"
        :disabled="showDownloadError"
        full-width
        icon-left
        size="lg"
        @click="buttonClickHandler"
      >
        {{ buttonLabel }}
        <template #icon>
          <IconDownload class="text-3xl" />
        </template>
      </ButtonText>

      <p
        v-if="isOwnedPaidPattern"
        class="text-center text-sm font-normal text-black-600"
      >
        {{ t('patterns.download.already_purchased') }}
      </p>
    </div>

    <WishlistButton
      :product-group-identifier="productGroupIdentifier"
      :sku="wishlistSku"
    />
  </div>

  <UserDetailsModal
    v-model:active="showPatternDownloadModal"
    :submit-label="t('patterns.download.modal.button_cta')"
    @submitted="onUserDetailsSubmitted"
    @field-filled="onUserDetailsFieldFilled"
  >
    <template #submit-icon>
      <IconDownload class="text-2xl" />
    </template>
    <template #header>
      <div class="flex w-full flex-col items-center gap-6 text-black">
        <div class="flex flex-col items-center gap-2 md:flex-row">
          <Image
            v-if="productImageUrl"
            :src="productImageUrl"
            :alt="productName"
            img-classes="size-10  overflow-hidden rounded-full"
            loader="fade"
            class="shrink-0"
          />

          <div class="mx-auto text-base leading-6">
            {{ truncatedProductName }}
          </div>
        </div>

        <h2 class="text-2xl font-bold leading-8">
          {{ t('patterns.download.modal.title') }}
        </h2>
        <div class="text-pretty text-center text-base leading-6">
          {{
            t('patterns.download.modal.description', {
              amount: formattedGiftCardPrice
            })
          }}
        </div>
      </div>
    </template>
    <template #footer>
      <div class="-mt-2 flex w-full flex-col items-center gap-10 text-black">
        <div class="text-xs">
          <a
            href="#"
            class="text-sm font-bold text-primary hover:text-primary-600"
            @click.prevent="patternDownloadAnonymous"
            >{{ t('patterns.download.modal.skip_form_cta') }}</a
          >
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="text-center text-xs text-[#000] [&_a]:font-bold [&_a]:text-[#000]"
          v-html="t('patterns.download.modal.disclaimer_html')"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </template>
  </UserDetailsModal>

  <AddPatternToStashModal
    v-model:active="showAddToStashModal"
    :loading="saveToStashLoading"
    :error="saveToStashError"
    @save-to-stash="onSaveToStash"
    @just-pdf="onJustPdf"
  />

  <PatternLanguageModal
    v-model:active="showPatternLanguageModal"
    :sku="productSku"
    :contact-uuid="emarsysUUID"
    @downloaded="onPatternLanguageDownloaded"
  />

  <Modal v-model:active="showDownloadSuccessModal" size="md">
    <template #body>
      <div class="flex flex-col gap-4 text-black">
        <h2 class="text-2xl font-bold leading-tight">
          {{ t('patterns.download.success_title') }}
        </h2>

        <PatternDownloadRetry
          :sku="productSku"
          :loading="isLoading"
          :error="retryErrorMessage"
          @retry="retryPaidDownload"
        />
      </div>
    </template>
  </Modal>
</template>
